import { useEffect, useState, useCallback } from 'react';
import confetti from 'canvas-confetti';

// Visual feedback types
export const FEEDBACK_TYPES = {
  DAMAGE: 'damage',
  HEAL: 'heal',
  ENERGY: 'energy',
  XP_GAIN: 'xp_gain',
  XP_LOSS: 'xp_loss',
  LEVEL_UP: 'level_up',
  EVOLUTION: 'evolution'
};

// Hook to manage visual feedback effects
export const useVisualFeedback = () => {
  const [activeEffects, setActiveEffects] = useState([]);
  const [screenFlash, setScreenFlash] = useState(null);

  const triggerEffect = useCallback((type, data = {}) => {
    const effectId = `effect_${Date.now()}_${Math.random()}`;
    
    // Add effect to active list
    setActiveEffects(prev => [...prev, { id: effectId, type, data }]);
    
    // Set screen flash based on type
    switch (type) {
      case FEEDBACK_TYPES.DAMAGE:
        setScreenFlash('damage');
        // Play damage sound effect (optional)
        playSound('damage');
        break;
      case FEEDBACK_TYPES.HEAL:
        setScreenFlash('heal');
        playSound('heal');
        break;
      case FEEDBACK_TYPES.ENERGY:
        setScreenFlash('energy');
        playSound('energy');
        break;
      case FEEDBACK_TYPES.XP_GAIN:
        setScreenFlash('xp');
        playSound('xp');
        // Trigger sparkles
        triggerSparkles('gold');
        break;
      case FEEDBACK_TYPES.XP_LOSS:
        setScreenFlash('xp-loss');
        break;
      case FEEDBACK_TYPES.LEVEL_UP:
        setScreenFlash('level-up');
        playSound('levelup');
        triggerConfetti('level');
        break;
      case FEEDBACK_TYPES.EVOLUTION:
        setScreenFlash('evolution');
        playSound('evolution');
        triggerConfetti('evolution');
        break;
      default:
        break;
    }
    
    // Clear screen flash after animation
    setTimeout(() => {
      setScreenFlash(null);
    }, 600);
    
    // Remove effect after duration
    setTimeout(() => {
      setActiveEffects(prev => prev.filter(e => e.id !== effectId));
    }, 2000);
    
  }, []);

  return { activeEffects, screenFlash, triggerEffect };
};

// Sound effects (using Web Audio API)
const playSound = (type) => {
  // Create audio context only when needed
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Different sounds for different effects
    switch (type) {
      case 'damage':
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        break;
      case 'heal':
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        break;
      case 'energy':
        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.15);
        gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        break;
      case 'xp':
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.35);
        break;
      case 'levelup':
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
        oscillator.frequency.setValueAtTime(1047, audioContext.currentTime + 0.3);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        break;
      case 'evolution':
        oscillator.type = 'sine';
        for (let i = 0; i < 8; i++) {
          oscillator.frequency.setValueAtTime(400 + i * 100, audioContext.currentTime + i * 0.08);
        }
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
        break;
      default:
        return;
    }
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.8);
  } catch (e) {
    // Audio not supported or blocked
    console.log('Audio feedback not available');
  }
};

// Trigger sparkle effects
const triggerSparkles = (color) => {
  const colors = {
    gold: ['#FFD700', '#FFA500', '#FFFF00'],
    green: ['#22C55E', '#10B981', '#34D399'],
    blue: ['#3B82F6', '#60A5FA', '#93C5FD'],
    red: ['#EF4444', '#F87171', '#FCA5A5']
  };
  
  confetti({
    particleCount: 30,
    spread: 60,
    startVelocity: 20,
    origin: { y: 0.6 },
    colors: colors[color] || colors.gold,
    gravity: 0.8,
    scalar: 0.8
  });
};

// Trigger confetti celebration
const triggerConfetti = (type) => {
  if (type === 'level') {
    // Level up confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#FFD700', '#FFA500', '#FF6347', '#9333EA', '#3B82F6']
    });
  } else if (type === 'evolution') {
    // Evolution confetti - more dramatic
    const duration = 2000;
    const end = Date.now() + duration;
    
    const frame = () => {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#FFD700', '#9333EA', '#3B82F6', '#22C55E']
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#FFD700', '#9333EA', '#3B82F6', '#22C55E']
      });
      
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  }
};

// Screen flash overlay component
export const ScreenFlash = ({ type }) => {
  if (!type) return null;
  
  const flashClasses = {
    damage: 'bg-red-500/30 animate-flash-damage',
    heal: 'bg-green-500/30 animate-flash-heal',
    energy: 'bg-yellow-500/30 animate-flash-energy',
    xp: 'bg-blue-500/20 animate-flash-xp',
    'xp-loss': 'bg-purple-500/20 animate-flash-damage',
    'level-up': 'bg-gradient-to-t from-yellow-500/40 to-transparent animate-flash-levelup',
    evolution: 'bg-gradient-to-t from-purple-500/50 to-blue-500/30 animate-flash-evolution'
  };
  
  return (
    <div 
      className={`fixed inset-0 pointer-events-none z-50 ${flashClasses[type] || ''}`}
      data-testid="screen-flash"
    />
  );
};

// Floating particles component for heal/energy effects
export const FloatingParticles = ({ type, isActive }) => {
  if (!isActive) return null;
  
  const particleColors = {
    heal: 'bg-green-400',
    energy: 'bg-yellow-400',
    xp: 'bg-blue-400'
  };
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className={`absolute w-2 h-2 rounded-full ${particleColors[type] || 'bg-white'} animate-float-up`}
          style={{
            left: `${20 + Math.random() * 60}%`,
            bottom: '10%',
            animationDelay: `${i * 0.1}s`,
            opacity: 0.8
          }}
        />
      ))}
    </div>
  );
};

// Stat bar with glow effect
export const GlowingStatBar = ({ 
  value, 
  maxValue, 
  color = 'blue', 
  isGlowing = false,
  label,
  icon,
  testId
}) => {
  const percentage = Math.min(100, (value / maxValue) * 100);
  
  const colorClasses = {
    red: 'from-red-500 to-pink-600',
    blue: 'from-blue-500 to-cyan-500',
    yellow: 'from-yellow-400 to-orange-500',
    green: 'from-green-500 to-emerald-500'
  };
  
  const glowClasses = {
    red: 'shadow-red-500/50',
    blue: 'shadow-blue-500/50',
    yellow: 'shadow-yellow-500/50',
    green: 'shadow-green-500/50'
  };
  
  return (
    <div className="stat-bar-container">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-nunito font-bold" style={{ color: 'var(--text-secondary)' }}>{label}</span>
        </div>
        <span 
          className={`font-nunito font-bold transition-all duration-300 ${isGlowing ? 'scale-110' : ''}`}
          style={{ color: 'var(--text-primary)' }}
        >
          {value}/{maxValue}
        </span>
      </div>
      <div 
        className="w-full rounded-full h-4 overflow-hidden"
        style={{ backgroundColor: 'var(--bg-tertiary)' }}
      >
        <div
          data-testid={testId}
          className={`bg-gradient-to-r ${colorClasses[color]} h-4 rounded-full transition-all duration-600 ease-out ${
            isGlowing ? `shadow-lg ${glowClasses[color]} animate-pulse-glow` : ''
          }`}
          style={{ 
            width: `${percentage}%`,
            transition: 'width 600ms ease-in-out, box-shadow 300ms ease-in-out'
          }}
        />
      </div>
    </div>
  );
};

// Card shake animation wrapper
export const ShakeWrapper = ({ children, isShaking, className = '' }) => {
  return (
    <div className={`${className} ${isShaking ? 'animate-shake' : ''}`}>
      {children}
    </div>
  );
};

// Export all components
export default {
  useVisualFeedback,
  ScreenFlash,
  FloatingParticles,
  GlowingStatBar,
  ShakeWrapper,
  FEEDBACK_TYPES
};
